import React, { useState } from 'react';
import ToolServerUsageTable from './ToolServerUsageTable';
import OrgUsageTable from './OrgUsageTable';
import UserUsageTable from './UserUsageTable';
import CallLogPanel from './CallLogPanel';
import '../../styles/BedrockUsage.css';

function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const today = new Date();
// Default start date: 2026-03-24 — first date with reliable tool server attribution tracking
const trackingStartDate = '2026-03-24';

type Level = 0 | 1 | 2 | 3;

const UsageTab: React.FC = () => {
  const [level, setLevel] = useState<Level>(0);
  const [selectedToolServer, setSelectedToolServer] = useState<{ id: number; name: string } | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<{ id: number; name: string } | null>(null);
  const [selectedUser, setSelectedUser] = useState<{ id: number; username: string } | null>(null);
  const [startDate, setStartDate] = useState<string>(trackingStartDate);
  const [endDate, setEndDate] = useState<string>(toDateInputValue(today));

  const handleSelectToolServer = (id: number, name: string) => {
    setSelectedToolServer({ id, name });
    setSelectedOrg(null);
    setSelectedUser(null);
    setLevel(1);
  };

  const handleSelectOrg = (id: number, name: string) => {
    setSelectedOrg({ id, name });
    setSelectedUser(null);
    setLevel(2);
  };

  const handleSelectUser = (id: number, username: string) => {
    setSelectedUser({ id, username });
    setLevel(3);
  };

  const goToLevel = (target: Level) => {
    if (target === 0) {
      setSelectedToolServer(null);
      setSelectedOrg(null);
      setSelectedUser(null);
    } else if (target === 1) {
      setSelectedOrg(null);
      setSelectedUser(null);
    } else if (target === 2) {
      setSelectedUser(null);
    }
    setLevel(target);
  };

  return (
    <div className="usage-tab">
      {/* Breadcrumb */}
      <div className="usage-breadcrumb">
        {level === 0 ? (
          <span className="breadcrumb-current">All Tool Servers</span>
        ) : (
          <button className="breadcrumb-item" onClick={() => goToLevel(0)}>
            All Tool Servers
          </button>
        )}

        {level >= 1 && selectedToolServer && (
          <>
            <span className="breadcrumb-separator">›</span>
            {level === 1 ? (
              <span className="breadcrumb-current">{selectedToolServer.name}</span>
            ) : (
              <button className="breadcrumb-item" onClick={() => goToLevel(1)}>
                {selectedToolServer.name}
              </button>
            )}
          </>
        )}

        {level >= 2 && selectedOrg && (
          <>
            <span className="breadcrumb-separator">›</span>
            {level === 2 ? (
              <span className="breadcrumb-current">{selectedOrg.name}</span>
            ) : (
              <button className="breadcrumb-item" onClick={() => goToLevel(2)}>
                {selectedOrg.name}
              </button>
            )}
          </>
        )}

        {level === 3 && selectedUser && (
          <>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">{selectedUser.username}</span>
          </>
        )}
      </div>

      {/* Date Range Filters */}
      <div className="usage-date-filters">
        <div>
          <label htmlFor="usage-start-date">Start Date</label>
          <input
            id="usage-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="usage-end-date">End Date</label>
          <input
            id="usage-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* Level Content */}
      {level === 0 && (
        <ToolServerUsageTable
          startDate={startDate}
          endDate={endDate}
          onSelectToolServer={handleSelectToolServer}
        />
      )}

      {level === 1 && selectedToolServer && (
        <OrgUsageTable
          toolServerId={selectedToolServer.id}
          toolServerName={selectedToolServer.name}
          startDate={startDate}
          endDate={endDate}
          onSelectOrg={handleSelectOrg}
        />
      )}

      {level === 2 && selectedToolServer && selectedOrg && (
        <UserUsageTable
          toolServerId={selectedToolServer.id}
          orgId={selectedOrg.id}
          orgName={selectedOrg.name}
          startDate={startDate}
          endDate={endDate}
          onSelectUser={handleSelectUser}
        />
      )}

      {level === 3 && selectedUser && selectedOrg && (
        <CallLogPanel
          userId={selectedUser.id}
          username={selectedUser.username}
          orgId={selectedOrg.id}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </div>
  );
};

export default UsageTab;
